import { zColor } from "@remotion/zod-types";
import { z } from "zod";

/**
 * The city guide: one city, a handful of places, each circled on the map by hand and shown for what
 * you see there. The camera stays at one height and glides from place to place -- zooming in and out
 * of every stop read as seasick, and a guide is about where the places are relative to each other.
 *
 * This schema is also the editor. Remotion Studio turns every field into a control -- the colours
 * into colour pickers, the enums into dropdowns, the numbers into sliders -- with the render updating
 * live, so a marketer can restyle a guide without touching code: `npx remotion studio`, pick
 * `city-guide`, edit on the right, render from the same page.
 */

export const CITY_GUIDE_MAP_STYLES = [
	"satellite",
	"stippl",
	"city",
	"alabaster",
	"adventure",
	"polaris",
	"nocturne",
	"obsidian",
	"espionage",
	"unicorn",
] as const;

export const CITY_GUIDE_CATEGORIES = ["landmark", "food", "shopping", "museum", "coffee", "nightlife", "beach", "nature", "viewpoint"] as const;

export const cityGuideSpotSchema = z.object({
	name: z.string(),
	lng: z.number(),
	lat: z.number(),
	/** A photo or a clip: a clip is anything ending in .mp4, .mov, .webm or .m4v. */
	media: z.string(),
	/** A still of the clip, for the outro's cards. The media itself for a photo. */
	poster: z.string(),
	/** Seconds into the clip where the good part starts. */
	startAt: z.number().min(0),
	category: z.enum(CITY_GUIDE_CATEGORIES),
	/** One line on the card. Short: it has to read in the time the card is up. */
	note: z.string(),
});

export const cityGuideSchema = z.object({
	title: z.string(),
	kicker: z.string(),
	/** The country, for the round flag. */
	country: z.string(),
	/**
	 * The product's own map styles, by the ids in constants/mapStyles.ts. Mapbox Standard is not on the
	 * list yet: in this composition its map never fired `load` (two stills, 148 s each), while the trip
	 * reel loads the same style fine on the same GL JS version. The cause is not found; until it is, a
	 * style that can hang a render is not offered.
	 */
	mapStyle: z.enum(CITY_GUIDE_MAP_STYLES),
	/** The hand-drawn circle. */
	circleColor: zColor(),
	circleWidth: z.number().min(6).max(30),
	/** 0 is nearly an ellipse, 1 a relaxed hand, 2 hurried. */
	circleLooseness: z.number().min(0).max(2),
	circleSize: z.number().min(0.6).max(1.6),
	/** Keep each circle on the map, faded, once the guide has moved on: the route builds up. */
	keepCircles: z.boolean(),
	labelFont: z.enum(["handwritten", "poppins"]),
	labelColor: zColor(),
	/** Number badges, the title's underline, the category chips. */
	accentColor: zColor(),
	photoStyle: z.enum(["card", "bubble"]),
	numbered: z.boolean(),
	secondsPerSpot: z.number().min(2).max(6),
	/** Metres the camera flies at. Kept, never changed mid-guide. */
	altitude: z.number().min(1500).max(40000),
	pitch: z.number().min(0).max(60),
	showOutro: z.boolean(),
	spots: z.array(cityGuideSpotSchema).min(1),
});

export type CityGuideProps = z.infer<typeof cityGuideSchema>;
export type CityGuideSpot = z.infer<typeof cityGuideSpotSchema>;

/** Bangkok, the old town and the river: coordinates and media from the curated places and gallery. */
export const BANGKOK_GUIDE: CityGuideProps = {
	title: "Bangkok",
	kicker: "5 spots · old town & river",
	country: "Thailand",
	mapStyle: "satellite",
	circleColor: "#e84d7e",
	circleWidth: 15,
	circleLooseness: 1,
	circleSize: 1,
	keepCircles: true,
	labelFont: "handwritten",
	labelColor: "#e84d7e",
	accentColor: "#13c892",
	photoStyle: "card",
	numbered: true,
	secondsPerSpot: 3.2,
	altitude: 5200,
	pitch: 45,
	showOutro: true,
	spots: [
		{
			name: "Grand Palace",
			lng: 100.4915765,
			lat: 13.7498558,
			media:
				"https://images.unsplash.com/photo-1583491470869-ca0b9fa90216?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3wxMTc1NDd8MHwxfHNlYXJjaHw0fHxCYW5na29rJTIwVGhhaWxhbmR8ZW58MHwxfHx8MTc4NjU1MjEyOXww&ixlib=rb-4.1.0&q=80&w=1080",
			poster:
				"https://images.unsplash.com/photo-1583491470869-ca0b9fa90216?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3wxMTc1NDd8MHwxfHNlYXJjaHw0fHxCYW5na29rJTIwVGhhaWxhbmR8ZW58MHwxfHx8MTc4NjU1MjEyOXww&ixlib=rb-4.1.0&q=80&w=1080",
			startAt: 0,
			category: "landmark",
			note: "Go at opening, before the heat",
		},
		{
			name: "Wat Pho",
			lng: 100.4927733,
			lat: 13.7464237,
			media:
				"https://images.unsplash.com/photo-1562602833-0f4ab2fc46e3?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3wxMTc1NDd8MHwxfHNlYXJjaHwyfHxCYW5na29rJTIwVGhhaWxhbmR8ZW58MHwxfHx8MTc4NjU1MjEyOXww&ixlib=rb-4.1.0&q=80&w=1080",
			poster:
				"https://images.unsplash.com/photo-1562602833-0f4ab2fc46e3?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3wxMTc1NDd8MHwxfHNlYXJjaHwyfHxCYW5na29rJTIwVGhhaWxhbmR8ZW58MHwxfHx8MTc4NjU1MjEyOXww&ixlib=rb-4.1.0&q=80&w=1080",
			startAt: 0,
			category: "landmark",
			note: "The 46 m golden reclining Buddha",
		},
		{
			name: "Wat Arun",
			lng: 100.488444,
			lat: 13.7438652,
			media: "https://videos.pexels.com/video-files/36401542/15436775_1080_2320_30fps.mp4",
			poster: "https://images.pexels.com/videos/36401542/pexels-photo-36401542.jpeg?auto=compress&cs=tinysrgb&fit=crop&h=1200&w=630",
			startAt: 1.5,
			category: "landmark",
			note: "Climb the porcelain spire",
		},
		{
			name: "Chinatown",
			lng: 100.5086773,
			lat: 13.7408624,
			media: "https://videos.pexels.com/video-files/31387500/13392980_1080_1920_60fps.mp4",
			poster: "https://images.pexels.com/videos/31387500/pexels-photo-31387500.jpeg?auto=compress&cs=tinysrgb&fit=crop&h=1200&w=630",
			startAt: 0.5,
			category: "food",
			note: "Street food after dark",
		},
		{
			name: "ICONSIAM",
			lng: 100.5106291,
			lat: 13.7262171,
			media: "https://videos.pexels.com/video-files/34438127/14592067_2160_3840_50fps.mp4",
			poster: "https://images.pexels.com/videos/34438127/asia-bangkok-big-city-city-walk-34438127.jpeg?auto=compress&cs=tinysrgb&fit=crop&h=1200&w=630",
			startAt: 2,
			category: "shopping",
			note: "Floating market with river views",
		},
	],
};

// ---- the timeline (frames at 30 fps) ------------------------------------------------------------
export const GUIDE_FPS = 30;
/** The title on its own before the first circle. */
export const TITLE_LEAD = 40;
/** Frames the camera takes to glide from one place to the next. */
export const PAN = 26;
/** A still beat on the last place before the outro. */
export const END_HOLD = 20;
export const OUTRO_FRAMES = 226;

export const spotFrames = (props: Pick<CityGuideProps, "secondsPerSpot">) => Math.round(props.secondsPerSpot * GUIDE_FPS);

/** Where place `i`'s time starts. Its first PAN frames (from the second place on) are the glide in. */
export const spotStart = (i: number, props: Pick<CityGuideProps, "secondsPerSpot">) => TITLE_LEAD + i * spotFrames(props);

/** The frame the camera settles on place `i`, and so the frame its circle starts. */
export const spotArrive = (i: number, props: Pick<CityGuideProps, "secondsPerSpot">) => spotStart(i, props) + (i > 0 ? PAN : 0);

/** The guide's length: the title, every place, a beat, and the outro if it has one. */
export const cityGuideFrames = (props: Pick<CityGuideProps, "secondsPerSpot" | "spots" | "showOutro">) =>
	TITLE_LEAD + props.spots.length * spotFrames(props) + END_HOLD + (props.showOutro ? OUTRO_FRAMES : 0);

/** Which two places the camera is between on `frame`, and how far along. */
export const cameraLeg = (frame: number, props: Pick<CityGuideProps, "secondsPerSpot" | "spots">) => {
	const n = props.spots.length;
	for (let i = n - 1; i >= 1; i--) {
		const s = spotStart(i, props);
		if (frame >= s) return { from: i - 1, to: i, t: Math.max(0, Math.min(1, (frame - s) / PAN)) };
	}
	return { from: 0, to: 0, t: 1 };
};
